import { getConfig } from '@/config';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';

export interface PublishFeaturedThumbnailRequest {
  sku: string;
  imageUrl: string;
}

/** Content types the product image bucket accepts; the presign is locked to the one requested. */
export const ALLOWED_PRODUCT_IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

interface SignedUploadUrl {
  presignedUrl: string;
  objectKey: string;
  cdnUrl: string;
}

export interface PublishFeaturedThumbnailResponse {
  success: boolean;
  sku: string;
  wpFeaturedImageUrl: string;
  message: string;
}

/**
 * Changes a product's featured image without WP Admin.
 *
 * The endpoint hands the URL to WordPress, which downloads the image itself, so nothing is uploaded
 * from the browser. It preserves the product's gallery and clears the product cache, which a WP
 * Admin edit does not do (SSD-6325).
 */
export class ProductMediaClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;

  constructor(baseUrl: string = getAdminApiUrl(AdminApiDomain.PRODUCT), apiKey: string = getConfig().apiKey) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
  }

  /**
   * Publishes `imageUrl` as the product's featured image and resolves with the URL the API serves
   * afterwards. Rejects with the server's message when the SKU is unknown or a variation, when the
   * image is unreachable, or when the publish itself fails.
   */
  public async publishFeaturedThumbnail(
    request: PublishFeaturedThumbnailRequest,
  ): Promise<PublishFeaturedThumbnailResponse> {
    const response = await fetch(`${this.baseUrl}/woocommerce/media/featured-thumbnail`, {
      method: 'POST',
      // internal-api guards this route with ApiKeyGuard, exactly as the other admin clients expect.
      headers: { 'Content-Type': 'application/json', 'X-API-Key': this.apiKey },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(await ProductMediaClient.readErrorMessage(response));
    }

    return (await response.json()) as PublishFeaturedThumbnailResponse;
  }

  /**
   * Uploads a local file to the product image bucket and resolves with the public URL it now has.
   *
   * Two steps, because WordPress can only fetch a URL: ask for a presigned PUT, send the bytes
   * straight to the bucket, then hand the returned `cdnUrl` to `publishFeaturedThumbnail`. The
   * bucket key carries a random suffix, so an upload cannot overwrite an existing object the way a
   * WP Admin upload can (SSD-6152).
   *
   * Rejects when the file type is not one the bucket accepts, or when either step fails.
   */
  public async uploadImage(file: File): Promise<string> {
    if (!ALLOWED_PRODUCT_IMAGE_CONTENT_TYPES.includes(file.type)) {
      throw new Error(`${file.type || 'This file type'} is not supported. Use a JPEG, PNG, WebP or GIF image.`);
    }

    const presignResponse = await fetch(`${this.baseUrl}/products/admin/images/presignedUrl`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': this.apiKey },
      body: JSON.stringify({ fileName: file.name, contentType: file.type }),
    });
    if (!presignResponse.ok) {
      throw new Error(await ProductMediaClient.readErrorMessage(presignResponse));
    }
    const signed = (await presignResponse.json()) as SignedUploadUrl;

    // The presigned URL is locked to the content type it was issued for, so the PUT must repeat it.
    // No API key here: the signature in the URL is the authorisation, and the bucket is not ours to
    // send credentials to.
    const uploadResponse = await fetch(signed.presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    return signed.cdnUrl;
  }

  /**
   * The server's own message where there is one, since it explains *why* a publish was refused —
   * an unreachable URL and a variation SKU are both 400 and the status alone cannot tell them apart.
   */
  private static async readErrorMessage(response: Response): Promise<string> {
    try {
      const body = await response.json();
      const message = body?.message;
      if (Array.isArray(message) && message.length > 0) {
        return message.join(', ');
      }
      if (typeof message === 'string' && message.length > 0) {
        return message;
      }
    } catch {
      // A non-JSON body (a gateway error page, say) leaves only the status line to report.
    }
    return `Failed to publish featured image: ${response.status} ${response.statusText}`;
  }
}
